import { formatInTimeZone } from "date-fns-tz";
import {
  safeDecodeRoadSenseListing,
  type ReportListItem,
  type ScopedRoadsenseReportListItem,
} from "@visioni/a3s-reports/client-messages";
import { formatCalendarDate } from "../../../utils/format-date";

/**
 * The RoadSense landing's rows: every (lifecycle, site) pair across the
 * tenant's reports. Kept free of React and `@src` imports so jest can test it.
 */

/**
 * One lifecycle as the site list needs it, whichever list route it came from:
 * the tenant-admin `{type}` list ({@link listRowFromReport}) or the
 * camera-scoped RoadSense list ({@link listRowFromScopedReport}). Both map onto
 * this, so there is one rule for grouping and sorting.
 */
export interface RoadSenseListRow {
  reportId: string;
  /** `YYYY-MM-DD`. */
  periodStart: string;
  /** `YYYY-MM-DD`. */
  periodEnd: string;
  status: ReportListItem["status"];
  /** The lifecycle's sites; empty when it has no readable site list yet. */
  sites: readonly RoadSenseListSite[];
  /** Whether the lifecycle has closed, when the route says so. `undefined` falls back to the date rule. */
  isFinal: boolean | undefined;
}

export interface RoadSenseListSite {
  siteId: string;
  siteName: string;
}

/** The fields of a `{type}` list row (`ReportListItem`) {@link listRowFromReport} reads. */
export interface RoadSenseReportListFields {
  reportId: string;
  /** `YYYY-MM-DD`. */
  periodStart: string;
  /** `YYYY-MM-DD`. */
  periodEnd: string;
  status: ReportListItem["status"];
  /** The row's opaque listing summary, decoded with `safeDecodeRoadSenseListing`. */
  listingSummary: unknown;
}

/** A `{type}` list row with its listing summary decoded. An undecodable summary reads as no sites. */
export function listRowFromReport(report: RoadSenseReportListFields): RoadSenseListRow {
  const listing = safeDecodeRoadSenseListing(report.listingSummary);
  return {
    reportId: report.reportId,
    periodStart: report.periodStart,
    periodEnd: report.periodEnd,
    status: report.status,
    sites: listing.success ? listing.data.sites : [],
    // A listing that says whether the lifecycle is final is authoritative; the
    // date rule only covers rows written before `isFinal` existed.
    isFinal: listing.success ? listing.data.isFinal : undefined,
  };
}

/** The fields of a camera-scoped list row (`ScopedRoadsenseReportListItem`) {@link listRowFromScopedReport} reads. */
export interface RoadSenseScopedReportListFields {
  reportId: string;
  /** `YYYY-MM-DD`. */
  periodStart: string;
  /** `YYYY-MM-DD`. */
  periodEnd: string;
  status: ScopedRoadsenseReportListItem["status"];
  sites: readonly RoadSenseListSite[];
  /** Absent when the server doesn't know (an admin row whose listing predates `isFinal`). */
  isFinal?: boolean | undefined;
}

/**
 * A camera-scoped list row. It carries only the caller's in-scope sites; its
 * `isFinal` is authoritative when present, else open/closed comes from the date rule.
 */
export function listRowFromScopedReport(report: RoadSenseScopedReportListFields): RoadSenseListRow {
  return {
    reportId: report.reportId,
    periodStart: report.periodStart,
    periodEnd: report.periodEnd,
    status: report.status,
    sites: report.sites,
    isFinal: report.isFinal,
  };
}

export interface RoadSenseSiteRow {
  /** Stable React key: one per (lifecycle, site), or per placeholder lifecycle. */
  key: string;
  reportId: string;
  /** Absent on a placeholder. */
  siteId?: string;
  /** Absent on a placeholder. */
  siteName?: string;
  /** "24 Apr 2026 – 15 Oct 2026" (up to the viewer's today) for an open lifecycle, its own dates for a closed one. */
  periodLabel: string;
  isOpen: boolean;
  /** Links to the site report. False only for a placeholder. */
  clickable: boolean;
  /**
   * Set on the one row standing in for a lifecycle that has never finished
   * generating (no decodable per-site list yet), which can't be opened.
   */
  placeholderStatus?: "generating" | "failed";
}

/** RoadSense's schedule zone: a lifecycle's stored `periodEnd` is a Sydney calendar date. */
const ROADSENSE_TIMEZONE = "Australia/Sydney";

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * One row per (lifecycle, site), with open lifecycles first, then closed ones
 * by start date (newest first), and each lifecycle's sites by name.
 *
 * A row with sites is clickable whatever the report's status: the site report
 * is served live from the stored job, so a daily rebuild (`generating`) must
 * not disable the list. Only a lifecycle with no sites — one that has never
 * completed a generation, or whose listing doesn't decode — becomes a single
 * placeholder.
 */
export function roadSenseSiteRows(
  reports: readonly RoadSenseListRow[],
  now: Date,
  viewerTimezone: string,
): RoadSenseSiteRow[] {
  // Open/closed follows the daily run's Sydney schedule; the label shows the
  // viewer's own today.
  const oldestOpenEnd = oldestOpenPeriodEnd(formatInTimeZone(now, ROADSENSE_TIMEZONE, "yyyy-MM-dd"));
  const viewerToday = formatInTimeZone(now, viewerTimezone, "yyyy-MM-dd");
  const lifecycles = reports.map((report) => {
    const isOpen = report.isFinal !== undefined ? !report.isFinal : report.periodEnd >= oldestOpenEnd;
    return { report, sites: [...report.sites], isOpen };
  });
  lifecycles.sort((a, b) => {
    if (a.isOpen !== b.isOpen) return a.isOpen ? -1 : 1;
    if (a.report.periodStart === b.report.periodStart) return 0;
    return a.report.periodStart > b.report.periodStart ? -1 : 1;
  });
  return lifecycles.flatMap(({ report, sites, isOpen }) => lifecycleRows(report, sites, isOpen, viewerToday));
}

/**
 * The oldest `periodEnd` a lifecycle without `isFinal` can have and still count
 * as open: Sydney today − 2 days. An open lifecycle's stored `periodEnd` is the
 * last completed Sydney day, but it only advances when the 06:00 tick runs, so
 * between midnight and the tick it is the day before yesterday.
 *
 * Known edges: a just-closed lifecycle reads as open (ending today) for up to
 * two days, and an open one whose tick has been missed for two or more days
 * reads as closed.
 */
function oldestOpenPeriodEnd(today: string): string {
  return new Date(Date.parse(`${today}T00:00:00Z`) - 2 * DAY_MS).toISOString().slice(0, 10);
}

function lifecycleRows(
  report: RoadSenseListRow,
  sites: RoadSenseListSite[],
  isOpen: boolean,
  today: string,
): RoadSenseSiteRow[] {
  const periodLabel = `${formatCalendarDate(report.periodStart)} – ${formatCalendarDate(
    isOpen ? today : report.periodEnd,
  )}`;

  if (sites.length === 0) {
    return [
      {
        key: `${report.reportId}:placeholder`,
        reportId: report.reportId,
        periodLabel,
        isOpen,
        clickable: false,
        // A ready row always carries its site list, so only a drifted one lands
        // here; it reads as unavailable rather than "being generated".
        placeholderStatus: report.status === "generating" ? "generating" : "failed",
      },
    ];
  }

  sites.sort((a, b) => {
    const byName = a.siteName.localeCompare(b.siteName, "en", { sensitivity: "base" });
    if (byName !== 0) return byName;
    if (a.siteId === b.siteId) return 0;
    return a.siteId < b.siteId ? -1 : 1;
  });
  return sites.map((site) => ({
    key: `${report.reportId}:${site.siteId}`,
    reportId: report.reportId,
    siteId: site.siteId,
    siteName: site.siteName,
    periodLabel,
    isOpen,
    clickable: true,
  }));
}
